import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import StatusBadge from "../../../components/StatusBadge";
import {
  avatarColor, initials, photoSrc, canSeeTab,
} from "./profileUtils";
import ProfileTab      from "./tabs/ProfileTab";
import FamilyTab       from "./tabs/FamilyTab";
import AcademicHistoryTab from "./tabs/AcademicHistoryTab";
import GradesTab       from "./tabs/GradesTab";
import AttendanceTab   from "./tabs/AttendanceTab";
import FinancesTab     from "./tabs/FinancesTab";
import HealthTab       from "./tabs/HealthTab";
import BooksTab        from "./tabs/BooksTab";
import DocumentsTab    from "./tabs/DocumentsTab";
import SportsTab       from "./tabs/SportsTab";
import SectionProfileModal from "../SectionProfileModal/SectionProfileModal";
import s from "./StudentProfileModal.module.css";

const ALL_TABS = [
  { key: "profile",    label: "Profile",          icon: "👤" },
  { key: "family",     label: "Family",            icon: "👨‍👩‍👧" },
  { key: "academic",   label: "Academic History",  icon: "📚" },
  { key: "grades",     label: "Grades",            icon: "📊" },
  { key: "attendance", label: "Attendance",         icon: "📋" },
  { key: "finances",   label: "Finances",           icon: "💰" },
  { key: "health",     label: "Health",             icon: "🏥" },
  { key: "books",      label: "Books",              icon: "📖" },
  { key: "documents",  label: "Documents",          icon: "📁" },
  { key: "sports",     label: "Sports",             icon: "🏆" },
];

/**
 * Comprehensive student profile modal — opened from the Master List tab.
 *
 * Props:
 *   open         boolean
 *   student      Student row (must have id; ideally photoUrl, names, etc.)
 *   sections     Array<Section>
 *   gradeLevels  Array<GradeLevel>
 *   schoolYears  Array<SchoolYear>
 *   semesters    Array<Semester>
 *   onClose      fn()
 */
export default function StudentProfileModal({
  open,
  student,
  sections    = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  onClose,
  onNavigateToStudent,
  onStudentUpdate,
  zIndex      = 1090,
}) {
  /* Role-filtered tab list — computed once per render cycle.
     The memoised deps only contain primitive values (the role string)
     so it won't thrash on parent re-renders. */
  const visibleTabs = useMemo(
    () => ALL_TABS.filter(t => canSeeTab(t.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* Active tab key — reset to the first visible tab each time a new
     student is opened (studentId changes). */
  const [activeTab,   setActiveTab]   = useState(visibleTabs[0]?.key || "profile");
  /* Section profile modal — opened when clicking the section pill in the header */
  const [sectionOpen, setSectionOpen] = useState(false);

  /* Lazy-load tracker: each tab's data fetch fires only once when the tab
     is first visited. A Set of loaded tab keys persists across tab switches
     within the same modal session. */
  const [loadedTabs, setLoadedTabs] = useState(() => new Set());

  /* Reset when a new student is opened */
  useEffect(() => {
    if (!open || !student?.id) return;
    const first = visibleTabs[0]?.key || "profile";
    setActiveTab(first);
    setLoadedTabs(new Set([first]));
    setSectionOpen(false);
  }, [open, student?.id, visibleTabs]);

  /* ESC closes + lock body scroll */
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  function switchTab(key) {
    setActiveTab(key);
    setLoadedTabs(prev => {
      if (prev.has(key)) return prev;
      return new Set([...prev, key]);
    });
  }

  if (!open || !student) return null;

  /* ── Header derived data ─────────────────────────────────────────── */
  const sectionById    = Object.fromEntries(sections.map(sec => [sec.id, sec]));
  const gradeLevelById = Object.fromEntries(gradeLevels.map(gl  => [gl.id,  gl]));
  const currentSection = student.currentSectionId
    ? sectionById[student.currentSectionId] : null;
  const currentGrade   = currentSection?.gradeLevelId
    ? gradeLevelById[currentSection.gradeLevelId] : null;

  const photo       = photoSrc(student.photoUrl);
  const avatarSeed  = [student.lastName, student.firstName].filter(Boolean).join(", ");
  const displayName = [student.firstName, student.middleName, student.lastName]
    .filter(Boolean).join(" ") || "(Unnamed)";

  /* ── Common props forwarded to every tab ─────────────────────────── */
  const tabProps = {
    studentId:        student.id,
    sections,
    gradeLevels,
    schoolYears,
    semesters,
    parentZIndex:     zIndex,
    onStudentUpdate,
  };

  return createPortal(
    <div
      className={s.backdrop}
      style={{ zIndex }}
      role="presentation">

      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spm-title">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className={s.header}>
          {/* Gold shimmer overlay */}
          <div className={s.headerShimmer} aria-hidden="true" />

          <div
            className={s.avatar}
            style={
              photo
                ? { backgroundImage: `url(${photo})` }
                : { backgroundColor: avatarColor(avatarSeed) }
            }
            aria-hidden="true">
            {!photo && initials(student.firstName, student.lastName)}
          </div>

          <div className={s.headerInfo}>
            <div className={s.eyebrow}>Student Record</div>
            <h2 id="spm-title" className={s.headerName}>{displayName}</h2>

            <div className={s.headerMeta}>
              {student.studentNumber && (
                <span className={s.studentNum}>{student.studentNumber}</span>
              )}
              <StatusBadge status={student.status} />
              {currentGrade && currentSection && (
                <button
                  type="button"
                  className={s.sectionPill}
                  onClick={() => setSectionOpen(true)}
                  title="View section profile">
                  {currentGrade.name} · {currentSection.name} ›
                </button>
              )}
            </div>

            <div className={s.headerContact}>
              {student.contact && (
                <span>
                  <span aria-hidden="true">📞</span> {student.contact}
                </span>
              )}
              {student.address && (
                <span>
                  <span aria-hidden="true">📍</span> {student.address}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close student profile">
            ✕
          </button>
        </div>

        {/* ── Tab bar ────────────────────────────────────────────── */}
        <div className={s.tabBar} role="tablist" aria-label="Student profile sections">
          {visibleTabs.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`spm-panel-${tab.key}`}
              id={`spm-tab-${tab.key}`}
              className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ""}`}
              onClick={() => switchTab(tab.key)}>
              <span className={s.tabIcon} aria-hidden="true">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Tab panels ─────────────────────────────────────────── */}
        <div className={s.body}>
          {visibleTabs.map(tab => (
            <div
              key={tab.key}
              id={`spm-panel-${tab.key}`}
              role="tabpanel"
              aria-labelledby={`spm-tab-${tab.key}`}
              className={activeTab === tab.key ? s.tabPanelActive : s.tabPanel}>

              {/* Only mount a tab's component after its first visit */}
              {loadedTabs.has(tab.key) && renderTab(tab.key, tabProps, student)}
            </div>
          ))}
        </div>
      </div>

      {/* Section profile — opens above this modal when section pill is clicked */}
      <SectionProfileModal
        open={sectionOpen && !!currentSection}
        section={currentSection}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        zIndex={zIndex + 5}
        onClose={() => setSectionOpen(false)}
        onViewStudent={onNavigateToStudent
          ? (st) => { setSectionOpen(false); onNavigateToStudent(st); }
          : undefined}
      />
    </div>,
    document.body
  );
}

/* ── Tab renderer ────────────────────────────────────────────────────────
   Keeps the JSX in the main component clean and avoids a giant switch
   block inline. */
function renderTab(key, commonProps, student) {
  switch (key) {
    case "profile":
      return (
        <ProfileTab
          student={student}
          gradeLevels={commonProps.gradeLevels}
          sections={commonProps.sections}
          onStudentUpdate={commonProps.onStudentUpdate}
        />
      );
    case "family":
      return <FamilyTab studentId={commonProps.studentId} />;
    case "academic":
      return (
        <AcademicHistoryTab
          studentId={commonProps.studentId}
          sections={commonProps.sections}
          gradeLevels={commonProps.gradeLevels}
          schoolYears={commonProps.schoolYears}
          semesters={commonProps.semesters}
          parentZIndex={commonProps.parentZIndex}
        />
      );
    case "grades":
      return (
        <GradesTab
          studentId={commonProps.studentId}
          schoolYears={commonProps.schoolYears}
          semesters={commonProps.semesters}
        />
      );
    case "attendance":
      return (
        <AttendanceTab
          studentId={commonProps.studentId}
          schoolYears={commonProps.schoolYears}
          semesters={commonProps.semesters}
        />
      );
    case "finances":
      return (
        <FinancesTab
          studentId={commonProps.studentId}
          schoolYears={commonProps.schoolYears}
          semesters={commonProps.semesters}
          studentName={[student.lastName, student.firstName].filter(Boolean).join(" ")}
          studentNumber={student.studentNumber || ""}
        />
      );
    case "health":
      return <HealthTab studentId={commonProps.studentId} />;
    case "books":
      return <BooksTab studentId={commonProps.studentId} />;
    case "documents":
      return <DocumentsTab studentId={commonProps.studentId} />;
    case "sports":
      return (
        <SportsTab
          studentId={commonProps.studentId}
          schoolYears={commonProps.schoolYears}
        />
      );
    default:
      return null;
  }
}
